import { CheckOutlined, CloseOutlined } from "@ant-design/icons"
import { App, Button, Flex, Form } from "antd"
import React, { type ReactNode, useState } from "react"

import {
  buildEditRowCells,
  rowCommitKeyDown
} from "../../../../../../../components/EntityTable/editable-row-cells.ts"
import { MODAL_CONFIRM_CONFIG } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { FormChangedWatcher } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import InputNumber from "../../../../../../../components/Input/InputNumber.tsx"
import { validateFormWithScroll } from "../../../../../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../../../../../utils/object/is-form-changed.ts"
import type { ProjectCommission } from "../../../../../redux/project-commissions/project-commissions.types.ts"
import { COMMISSION_COLUMN_KEYS } from "./columns.tsx"

const percentEditor = (name: keyof ProjectCommission) => (
  <td className="ant-table-cell">
    <Form.Item<ProjectCommission> name={name} noStyle>
      <InputNumber
        min={0}
        max={100}
        precision={2}
        className="w-full"
        suffix="%"
        placeholder="0.00"
      />
    </Form.Item>
  </td>
)

interface ProjectViewCommissionsNewRowProps {
  children?: ReactNode[]
  record: ProjectCommission
  onSelectRow: (
    record: ProjectCommission,
    action: string
  ) => Promise<void> | void
}

export const ProjectViewCommissionsNewRow = ({
  record,
  onSelectRow,
  ...rest
}: ProjectViewCommissionsNewRowProps) => {
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<ProjectCommission>()
  const { modal } = App.useApp()

  const handleSave = async () => {
    try {
      const values = await validateFormWithScroll(form)
      setSaving(true)
      await onSelectRow({ ...record, ...values }, "update")
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const isChanged = isFormChanged<ProjectCommission>(
      form.getFieldsValue(),
      record
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          onSelectRow(record, "cancel")
        }
      })
    } else {
      onSelectRow(record, "cancel")
    }
  }

  const onKeyDownRow = rowCommitKeyDown(() => void handleSave(), {
    busy: saving
  })

  return (
    <Form<ProjectCommission>
      form={form}
      component={false}
      initialValues={record}
      disabled={saving}
    >
      <FormChangedWatcher<ProjectCommission> form={form} saving={saving} />

      <tr {...rest} onKeyDown={onKeyDownRow}>
        {buildEditRowCells(
          rest?.children,
          Object.values(COMMISSION_COLUMN_KEYS),
          {
            [COMMISSION_COLUMN_KEYS.officeMainLeadCost]: percentEditor(
              "office_main_lead_cost"
            ),
            [COMMISSION_COLUMN_KEYS.officeReferralLeadCost]: percentEditor(
              "office_referral_lead_cost"
            ),
            [COMMISSION_COLUMN_KEYS.officeOtherLeadCost]: percentEditor(
              "office_other_lead_cost"
            ),
            [COMMISSION_COLUMN_KEYS.salesCommissionRatio]: percentEditor(
              "sales_commission_ratio"
            )
          },
          <td className="sticky right-0 ant-table-cell ant-table-cell-fix-right ant-table-cell-fix-right-first">
            <Flex gap="small" justify="center">
              <Button
                variant="outlined"
                color="danger"
                onClick={handleCancel}
                icon={<CloseOutlined />}
              />
              <Button
                loading={saving}
                variant="outlined"
                color="green"
                onClick={handleSave}
                icon={<CheckOutlined />}
              />
            </Flex>
          </td>
        )}
      </tr>
    </Form>
  )
}
